import axios, { AxiosInstance, AxiosResponse } from 'axios';

export type StockUnitType = 'UN' | 'KG' | 'G' | 'L' | 'ML' | 'CX' | 'DZ' | 'CUSTOM' | string;

export interface StockRecipeComponent {
  componentItemId: string;
  quantity: number;
  unit: StockUnitType;
  yieldPercentage?: number;
}

export interface StockItem {
  id: string;
  cnpj: string;
  name: string;
  sku?: string;
  unit: StockUnitType;
  conversionUnit?: StockUnitType;
  conversionRate?: number;
  minimumQuantity?: number;
  maximumQuantity?: number;
  category?: string;
  isActive: boolean;
  notes?: string;
  costMethod?: 'FIFO';
  recipeComponents?: StockRecipeComponent[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateStockItemRequest {
  name: string;
  sku?: string;
  unit: StockUnitType;
  conversionUnit?: StockUnitType;
  conversionRate?: number;
  minimumQuantity?: number;
  maximumQuantity?: number;
  category?: string;
  isActive?: boolean;
  notes?: string;
  recipeComponents?: StockRecipeComponent[];
}

export interface UpdateStockItemRequest extends Partial<CreateStockItemRequest> {}

export type StockMovementType =
  | 'ENTRY'
  | 'SALE_CONSUMPTION'
  | 'INTERNAL_CONSUMPTION'
  | 'TRANSFER_OUT'
  | 'TRANSFER_IN'
  | 'ADJUSTMENT_POSITIVE'
  | 'ADJUSTMENT_NEGATIVE';

export type StockMovementSource = 'MANUAL' | 'ORDER' | 'CLOSURE' | 'TRANSFER' | 'PRODUCTION' | 'SYSTEM';

export interface StockMovementCostInfo {
  unitCost: number;
  totalCost: number;
  currency?: string;
  lotId?: string;
  batchCode?: string;
  expirationDate?: string;
}

export interface StockMovement {
  id: string;
  cnpj: string;
  itemId: string;
  unitId?: string;
  unit: string;
  quantity: number;
  type: StockMovementType;
  source: StockMovementSource;
  referenceId?: string;
  referenceCode?: string;
  notes?: string;
  eventDate: string;
  costInfo?: StockMovementCostInfo;
  createdBy?: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface ListStockMovementsResponse {
  items: StockMovement[];
  lastEvaluatedKey?: Record<string, unknown>;
}

export interface RegisterStockMovementRequest {
  itemId: string;
  unit: string;
  quantity: number;
  type: StockMovementType;
  source: StockMovementSource;
  unitId?: string;
  referenceId?: string;
  referenceCode?: string;
  notes?: string;
  eventDate?: string;
  metadata?: Record<string, unknown>;
  costInfo?: Partial<StockMovementCostInfo>;
}

export interface StockBalance {
  cnpj: string;
  itemId: string;
  unitId?: string;
  unit: string;
  quantity: number;
  reservedQuantity?: number;
  availableQuantity?: number;
  averageCost?: number;
  fifoLayers?: Array<{
    lotId: string;
    quantity: number;
    unitCost: number;
    receivedAt?: string;
  }>;
  lastMovementAt?: string;
  updatedAt: string;
}

export type StockClosureStatus = 'DRAFT' | 'IN_PROGRESS' | 'APPROVED' | 'REJECTED';

export interface StockClosureCount {
  itemId: string;
  unit: string;
  expectedQuantity: number;
  countedQuantity: number;
  differenceQuantity: number;
  differenceCost?: number;
  notes?: string;
}

export interface StockClosure {
  id: string;
  cnpj: string;
  reference: string;
  unitId?: string;
  status: StockClosureStatus;
  startedBy: string;
  approvedBy?: string;
  rejectedBy?: string;
  startDate: string;
  endDate?: string;
  counts: StockClosureCount[];
  notes?: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface CreateStockClosureRequest {
  reference: string;
  unitId?: string;
  startedBy?: string;
  startDate?: string;
  counts?: StockClosureCount[];
  notes?: string;
  metadata?: Record<string, unknown>;
}

export interface UpdateStockClosureRequest {
  reference?: string;
  unitId?: string;
  status?: StockClosureStatus;
  approvedBy?: string;
  rejectedBy?: string;
  endDate?: string;
  counts?: StockClosureCount[];
  notes?: string;
  metadata?: Record<string, unknown>;
}

class StockService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3000/api',
      timeout: 15000,
    });

    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('accessToken');
        if (token) {
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error),
    );
  }

  async listStockItems(params?: {
    cnpj?: string;
    searchTerm?: string;
    category?: string;
    isActive?: boolean;
  }): Promise<StockItem[]> {
    try {
      const response: AxiosResponse<StockItem[]> = await this.client.get('/admin/stock/items', {
        params,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao buscar itens de estoque');
    }
  }

  async getStockItem(id: string, cnpj?: string): Promise<StockItem> {
    try {
      const response: AxiosResponse<StockItem> = await this.client.get(`/admin/stock/items/${id}`, {
        params: cnpj ? { cnpj } : undefined,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao carregar item de estoque');
    }
  }

  async createStockItem(data: CreateStockItemRequest): Promise<StockItem> {
    try {
      const response: AxiosResponse<StockItem> = await this.client.post('/admin/stock/items', data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao criar item de estoque');
    }
  }

  async updateStockItem(id: string, data: UpdateStockItemRequest): Promise<StockItem> {
    try {
      const response: AxiosResponse<StockItem> = await this.client.put(`/admin/stock/items/${id}`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao atualizar item de estoque');
    }
  }

  async deleteStockItem(id: string, params?: { cnpj?: string }): Promise<void> {
    try {
      await this.client.delete(`/admin/stock/items/${id}`, {
        params,
      });
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao excluir item de estoque');
    }
  }

  async listStockMovements(params?: {
    cnpj?: string;
    itemId?: string;
    unitId?: string;
    type?: StockMovementType;
    startDate?: string;
    endDate?: string;
    limit?: number;
  }): Promise<ListStockMovementsResponse> {
    try {
      const response: AxiosResponse<ListStockMovementsResponse> = await this.client.get(
        '/admin/stock/movements',
        { params },
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao listar movimentos de estoque');
    }
  }

  async registerStockMovement(data: RegisterStockMovementRequest): Promise<{ movement: StockMovement; balance: StockBalance }> {
    try {
      const response: AxiosResponse<{ movement: StockMovement; balance: StockBalance }> = await this.client.post(
        '/admin/stock/movements',
        data,
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao registrar movimento de estoque');
    }
  }

  async listStockBalances(params?: { cnpj?: string }): Promise<StockBalance[]> {
    try {
      const response: AxiosResponse<StockBalance[]> = await this.client.get('/admin/stock/balances', {
        params,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao listar saldos de estoque');
    }
  }

  async listStockClosures(params?: {
    cnpj?: string;
    status?: StockClosureStatus;
    unitId?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<StockClosure[]> {
    try {
      const response: AxiosResponse<StockClosure[]> = await this.client.get('/admin/stock/closures', {
        params,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao listar inventários');
    }
  }

  async createStockClosure(data: CreateStockClosureRequest): Promise<StockClosure> {
    try {
      const response: AxiosResponse<StockClosure> = await this.client.post('/admin/stock/closures', data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao iniciar inventário');
    }
  }

  async updateStockClosure(id: string, data: UpdateStockClosureRequest): Promise<StockClosure> {
    try {
      const response: AxiosResponse<StockClosure> = await this.client.put(`/admin/stock/closures/${id}`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao atualizar inventário');
    }
  }
}

export const stockService = new StockService();
export default stockService;
